import { ValueItems } from "Components/Shared/ValuesBox/ValueItems";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import { PartyType, type UserVerificationDto } from "Infrastructure/Api/Api";
import { useCountryNameResolver } from "Translations/countryNames";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Account;

type Props = {
  verificationData: UserVerificationDto;
  partyType: PartyType;
};

export const VerificationsSection = ({
  verificationData: {
    taxResidency,
    businessActivity,
    sourcesOfIncome,
    expectedMonthlyRevenue,
    averageTransactionValue,
    isPoliticallyExposedPerson,
    hasNonEUResidence,
    hasNonEUNationality,
    legacySourceOfIncome,
  },
  partyType,
}: Props) => {
  const { t } = useResource();
  const { getCountryName } = useCountryNameResolver();

  const isBusinessParty = partyType !== PartyType.NaturalPerson;

  const sourcesOfIncomeDisplay =
    sourcesOfIncome && sourcesOfIncome.length > 0
      ? sourcesOfIncome
          .map((source) => t(Resources.Form.SourceOfIncome[source]))
          .join(", ")
      : t(Resources.Common.Unknown);

  return (
    <ValuesBox title={t(COMPONENT_RESOURCES.Verification)}>
      {isBusinessParty && (
        <ValueItems
          values={[
            {
              label: t(COMPONENT_RESOURCES.TaxResidence),
              value:
                getCountryName(taxResidency) || t(Resources.Common.Unknown),
            },
            {
              label: t(COMPONENT_RESOURCES.BusinessSubject),
              value: businessActivity
                ? t(Resources.Form.BusinessActivity[businessActivity])
                : t(Resources.Common.Unknown),
            },
            {
              label: t(COMPONENT_RESOURCES.IncomeSource),
              value: sourcesOfIncomeDisplay,
            },
            {
              label: t(COMPONENT_RESOURCES.LegacyIncomeSource),
              value: legacySourceOfIncome || t(Resources.Common.Unknown),
            },
            {
              label: t(COMPONENT_RESOURCES.ExpectedMonthlyVolume),
              value: expectedMonthlyRevenue
                ? t(
                    Resources.Form.ExpectedMonthlyRevenue[
                      expectedMonthlyRevenue
                    ],
                  )
                : t(Resources.Common.Unknown),
            },
            {
              label: t(COMPONENT_RESOURCES.AverageTransactionValue),
              value: averageTransactionValue
                ? t(
                    Resources.Form.AverageTransactionValue[
                      averageTransactionValue
                    ],
                  )
                : t(Resources.Common.Unknown),
            },
          ]}
        />
      )}
      <ValueItems
        values={[
          {
            label: t(COMPONENT_RESOURCES.PoliticallyExposed),
            value: isPoliticallyExposedPerson
              ? t(Resources.Common.Yes)
              : t(Resources.Common.No),
          },
          {
            label: t(COMPONENT_RESOURCES.NonEUPermanentResidence),
            value: hasNonEUResidence
              ? t(Resources.Common.Yes)
              : t(Resources.Common.No),
          },
          {
            label: t(COMPONENT_RESOURCES.NonEUNationality),
            value: hasNonEUNationality
              ? t(Resources.Common.Yes)
              : t(Resources.Common.No),
          },
        ]}
      />
    </ValuesBox>
  );
};
